if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mobileZoomAbortController = null;
        this.mobileZoomImage = null;
        this.ensureMobileZoomStyles();
      }

      hide() {
        this.resetMobileZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.openedBy && this.openedBy.getAttribute('data-media-id');
        const container = this.querySelector('[role="document"]');
        if (!mediaId || !container) return;

        const modalMedia = Array.from(container.children).filter((element) => element.hasAttribute('data-media-id'));
        modalMedia.forEach((element) => {
          element.classList.toggle('active', element.getAttribute('data-media-id') === mediaId);
        });

        const activeMedia = modalMedia.find((element) => element.getAttribute('data-media-id') === mediaId);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        const isMobile = window.matchMedia('(max-width: 749px)').matches;

        if (isMobile) {
          container.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          this.setupMobileZoom(activeMedia);
        } else {
          activeMedia.scrollIntoView();
          container.scrollLeft = ((activeMedia.width || activeMedia.clientWidth || 0) - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      ensureMobileZoomStyles() {
        if (document.getElementById('laq-mobile-product-zoom-styles')) return;

        const style = document.createElement('style');
        style.id = 'laq-mobile-product-zoom-styles';
        style.textContent = `
          @media screen and (max-width: 749px) {
            product-modal.product-media-modal[open] .product-media-modal__dialog {
              height: 100dvh;
              overflow: hidden;
            }

            product-modal.product-media-modal[open] .product-media-modal__content {
              width: 100%;
              height: 100dvh;
              max-height: 100dvh;
              padding: 0 !important;
              overflow: hidden !important;
              display: flex;
              align-items: center;
              justify-content: center;
              overscroll-behavior: contain;
            }

            product-modal.product-media-modal[open] .product-media-modal__content > [data-media-id] {
              display: none !important;
            }

            product-modal.product-media-modal[open] .product-media-modal__content > [data-media-id].active {
              display: block !important;
              width: 100vw !important;
              max-width: 100vw !important;
              margin: 0 !important;
              flex: 0 0 100vw !important;
            }

            product-modal.product-media-modal[open] img.laq-mobile-zoom-target {
              display: block !important;
              width: 100vw !important;
              min-width: 100vw !important;
              max-width: 100vw !important;
              height: auto !important;
              max-height: none !important;
              margin: 0 !important;
              aspect-ratio: auto !important;
              object-fit: contain !important;
              object-position: center center !important;
              touch-action: none;
              user-select: none;
              -webkit-user-select: none;
              -webkit-user-drag: none;
              transform-origin: center center;
              will-change: transform;
              cursor: zoom-in;
            }

            product-modal.product-media-modal[open] img.laq-mobile-zoom-target.is-zoomed {
              cursor: grab;
            }
          }
        `;
        document.head.appendChild(style);
      }

      setupMobileZoom(activeMedia) {
        this.resetMobileZoom();

        const image = activeMedia.tagName === 'IMG' ? activeMedia : activeMedia.querySelector('img');
        if (!image) return;

        this.mobileZoomImage = image;
        image.classList.add('laq-mobile-zoom-target');

        const state = {
          scale: 1,
          x: 0,
          y: 0,
          startScale: 1,
          startDistance: 0,
          startX: 0,
          startY: 0,
          startTranslateX: 0,
          startTranslateY: 0,
          moved: false,
          lastTap: 0,
        };

        const controller = new AbortController();
        this.mobileZoomAbortController = controller;
        const signal = controller.signal;

        const distance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

        const clampPan = () => {
          if (state.scale <= 1) {
            state.x = 0;
            state.y = 0;
            return;
          }
          const maxX = Math.max(0, (image.clientWidth * (state.scale - 1)) / 2);
          const maxY = Math.max(0, (image.clientHeight * (state.scale - 1)) / 2);
          state.x = clamp(state.x, -maxX, maxX);
          state.y = clamp(state.y, -maxY, maxY);
        };

        const applyTransform = (animate = false) => {
          clampPan();
          image.style.transition = animate ? 'transform 220ms ease' : 'none';
          image.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;
          image.classList.toggle('is-zoomed', state.scale > 1.01);
        };

        const reset = (animate = true) => {
          state.scale = 1;
          state.x = 0;
          state.y = 0;
          applyTransform(animate);
        };

        image.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length === 2) {
              state.startDistance = distance(event.touches[0], event.touches[1]);
              state.startScale = state.scale;
              state.moved = true;
              image.style.transition = 'none';
              return;
            }

            if (event.touches.length === 1) {
              state.startX = event.touches[0].clientX;
              state.startY = event.touches[0].clientY;
              state.startTranslateX = state.x;
              state.startTranslateY = state.y;
              state.moved = false;
            }
          },
          { passive: true, signal }
        );

        image.addEventListener(
          'touchmove',
          (event) => {
            if (event.touches.length === 2 && state.startDistance > 0) {
              event.preventDefault();
              const nextDistance = distance(event.touches[0], event.touches[1]);
              state.scale = clamp(state.startScale * (nextDistance / state.startDistance), 1, 4);
              applyTransform(false);
              return;
            }

            if (event.touches.length === 1 && state.scale > 1.01) {
              event.preventDefault();
              const dx = event.touches[0].clientX - state.startX;
              const dy = event.touches[0].clientY - state.startY;
              if (Math.abs(dx) > 3 || Math.abs(dy) > 3) state.moved = true;
              state.x = state.startTranslateX + dx;
              state.y = state.startTranslateY + dy;
              applyTransform(false);
            }
          },
          { passive: false, signal }
        );

        image.addEventListener(
          'touchend',
          (event) => {
            if (event.touches.length > 0) return;

            if (state.scale < 1.05) {
              reset(true);
            }

            if (!state.moved && event.changedTouches.length === 1) {
              const now = Date.now();
              if (now - state.lastTap < 300) {
                if (state.scale > 1.01) {
                  reset(true);
                } else {
                  state.scale = 2.5;
                  state.x = 0;
                  state.y = 0;
                  applyTransform(true);
                }
                state.lastTap = 0;
              } else {
                state.lastTap = now;
              }
            }

            state.startDistance = 0;
          },
          { passive: true, signal }
        );

        image.addEventListener(
          'dragstart',
          (event) => event.preventDefault(),
          { signal }
        );

        applyTransform(false);
      }

      resetMobileZoom() {
        if (this.mobileZoomAbortController) {
          this.mobileZoomAbortController.abort();
          this.mobileZoomAbortController = null;
        }

        if (this.mobileZoomImage) {
          this.mobileZoomImage.classList.remove('laq-mobile-zoom-target', 'is-zoomed');
          this.mobileZoomImage.style.removeProperty('transform');
          this.mobileZoomImage.style.removeProperty('transition');
          this.mobileZoomImage = null;
        }
      }
    }
  );
}
